import type { RecordingCaptureMetadata } from "./recording-db"
import { RecordingHealth, type CaptureWarning } from "./recording-health"

export class RecordingCapture {
  private health = new RecordingHealth(Date.now())
  private stopped: Promise<void>
  private resolveStopped!: () => void
  private stopping = false
  private ended = false
  private finalMetadata?: RecordingCaptureMetadata
  private timer?: ReturnType<typeof setInterval>
  private audioContext?: AudioContext
  private analyser?: AnalyserNode
  private source?: MediaStreamAudioSourceNode
  private samples = new Float32Array(2048)
  private warning: CaptureWarning = null
  private emittedChunks = 0

  constructor(
    readonly recorder: MediaRecorder,
    private options: {
      onChunk: (blob: Blob) => void
      onInterrupted: () => void
      onWarning: (
        warning: CaptureWarning,
        diagnostics?: {
          emittedChunks: number
          peak: number
          audioContextState?: AudioContextState
          trackEnabled?: boolean
          trackMuted?: boolean
          trackReadyState?: MediaStreamTrackState
        }
      ) => void
      releaseCaptureLock: () => void
    }
  ) {
    this.stopped = new Promise((resolve) => {
      this.resolveStopped = resolve
    })
    try {
      this.audioContext = new AudioContext()
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = this.samples.length
      this.source = this.audioContext.createMediaStreamSource(recorder.stream)
      this.source.connect(this.analyser)
    } catch {
      // Monitoring is advisory; recording must still work when Web Audio cannot initialize.
      this.source?.disconnect()
      void this.audioContext?.close().catch(() => undefined)
      this.audioContext = undefined
      this.analyser = undefined
      this.source = undefined
    }
    recorder.addEventListener("dataavailable", this.onData)
    recorder.addEventListener("stop", this.onStop)
    recorder.addEventListener("error", this.onError)
    recorder.stream.getAudioTracks().forEach((track) => track.addEventListener("ended", this.onTrackEnded))
  }

  start() {
    this.health = new RecordingHealth(Date.now())
    try {
      this.recorder.start(10_000)
    } catch (error) {
      this.dispose()
      this.resolveStopped()
      throw error
    }
    this.timer = setInterval(this.sample, 500)
    document.addEventListener("visibilitychange", this.sample)
    window.addEventListener("pageshow", this.sample)
    window.addEventListener("pointerdown", this.resumeAudioContext)
    window.addEventListener("keydown", this.resumeAudioContext)
    this.resumeAudioContext()
  }

  pause() {
    if (this.recorder.state !== "recording" || this.stopping) return
    this.health.pause(Date.now())
    this.recorder.pause()
  }

  resume() {
    if (this.recorder.state !== "paused" || this.stopping) return
    this.health.resume(Date.now())
    this.recorder.resume()
    this.resumeAudioContext()
  }

  async stop(reason?: string): Promise<RecordingCaptureMetadata> {
    if (reason) this.health.interrupt(reason)
    if (!this.stopping) {
      this.stopping = true
      if (this.recorder.state !== "inactive") this.recorder.stop()
    }
    // Even an already-inactive recorder can still have its final dataavailable/stop events queued.
    await this.stopped
    this.finalMetadata ??= this.health.metadata(Date.now())
    return this.finalMetadata
  }

  private onData = (event: BlobEvent) => {
    if (event.data.size === 0) return
    this.health.chunk(Date.now())
    this.emittedChunks++
    this.options.onChunk(event.data)
    // Reserve the server's extra sequence for the final buffered data.
    if (this.emittedChunks === 1440 && !this.stopping) this.options.onInterrupted()
  }

  private onStop = () => {
    this.ended = true
    this.dispose()
    this.resolveStopped()
    if (!this.stopping) {
      this.health.interrupt("recorder_stopped")
      this.options.onInterrupted()
    }
  }

  private onError = () => {
    this.health.interrupt("recorder_error")
    this.options.onInterrupted()
  }

  private onTrackEnded = () => {
    if (this.stopping || this.ended) return
    this.health.interrupt("microphone_disconnected")
    this.options.onInterrupted()
  }

  private resumeAudioContext = () => {
    if (this.audioContext?.state === "suspended") {
      void this.audioContext.resume().catch(() => this.options.onWarning("unavailable"))
    }
  }

  private sample = () => {
    if (this.stopping || this.ended) return
    const track = this.recorder.stream.getAudioTracks()[0]
    let peak = 0
    if (this.audioContext?.state === "running" && track?.enabled && !track.muted && track.readyState === "live") {
      this.analyser?.getFloatTimeDomainData(this.samples)
      for (const sample of this.samples) peak = Math.max(peak, Math.abs(sample))
    }
    const signal = this.audioContext?.state !== "running" ? "unavailable" : peak > 0.001 ? "signal" : "silent"
    const warning = this.health.sample(Date.now(), signal)
    if (warning !== this.warning) {
      this.warning = warning
      this.options.onWarning(warning, {
        emittedChunks: this.emittedChunks,
        peak,
        audioContextState: this.audioContext?.state,
        trackEnabled: track?.enabled,
        trackMuted: track?.muted,
        trackReadyState: track?.readyState,
      })
    }
    if ((this.health.metadata(Date.now()).duration_ms ?? 0) >= 4 * 60 * 60 * 1000) this.options.onInterrupted()
  }

  private dispose() {
    this.options.releaseCaptureLock()
    clearInterval(this.timer)
    document.removeEventListener("visibilitychange", this.sample)
    window.removeEventListener("pageshow", this.sample)
    window.removeEventListener("pointerdown", this.resumeAudioContext)
    window.removeEventListener("keydown", this.resumeAudioContext)
    this.recorder.removeEventListener("dataavailable", this.onData)
    this.recorder.removeEventListener("stop", this.onStop)
    this.recorder.removeEventListener("error", this.onError)
    this.recorder.stream.getTracks().forEach((track) => {
      track.removeEventListener("ended", this.onTrackEnded)
      track.stop()
    })
    this.source?.disconnect()
    void this.audioContext?.close().catch(() => undefined)
  }
}
